import React, { useState, useRef, useEffect } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { getProjects } from '../data/projectsData';

const ProjectCard = ({ project, index }) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const handleMouseMove = (e) => {
    if (isTouchDevice || !cardRef.current) return;
    const { left, top, width, height } = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - left - width / 2) / (width / 2);
    const y = (e.clientY - top - height / 2) / (height / 2);
    
    // Smooth tilt calculation
    setTilt({ x: x * 15, y: -y * 15 });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
    setIsHovered(false);
  };

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ 
        duration: 0.6, 
        delay: index * 0.15,
        ease: [0.16, 1, 0.3, 1]
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={() => setIsHovered(true)}
      style={{
        perspective: "1000px",
        willChange: isHovered ? "transform" : "auto"
      }}
      className="relative h-full"
    >
      <motion.div
        animate={{ 
          rotateX: tilt.y, 
          rotateY: tilt.x,
          scale: isTouchDevice && isHovered ? 1.02 : 1
        }}
        transition={isHovered ? { type: "tween", ease: "linear", duration: 0 } : { type: "spring", stiffness: 300, damping: 20 }}
        style={{ transformStyle: "preserve-3d" }}
        className={`relative h-full bg-[#2c456b]/35 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden flex flex-col transition-shadow duration-300 shadow-xl ${isHovered ? 'border-[#4779c4]/60 shadow-[0_0_30px_rgba(71,121,196,0.35)] bg-[#2c456b]/45' : ''}`}
      >
        {/* Glow Pulse Effect */}
        {isHovered && (
          <div className="absolute inset-0 z-0 pointer-events-none animate-pulse-glow" />
        )}

        {/* Featured Badge */}
        {project.featured && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.15 + 0.4, type: "spring", stiffness: 200, damping: 10 }}
            className="absolute top-4 left-4 z-30 bg-[#4779c4] text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1 animate-bob shimmer-badge overflow-hidden"
          >
            <span>FEATURED</span>
          </motion.div>
        )}

        {/* Project Image Section */}
        <div 
          className="relative h-48 overflow-hidden bg-slate-100 dark:bg-slate-900 group"
          style={{ transform: isHovered ? "translateZ(20px)" : "none" }}
        >
          <motion.img 
            src={project.image} 
            alt={project.title}
            animate={{ scale: isHovered ? 1.05 : 1 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="w-full h-full object-cover opacity-100 transition-opacity"
          />
          
          {/* Overlay Buttons */}
          <div className={`absolute inset-0 bg-black/10 flex items-center justify-center gap-4 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            <motion.a
              href={project.links.github}
              target="_blank"
              animate={isHovered ? { scale: 1 } : { scale: 0 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
              className="w-10 h-10 bg-[#1b2d48]/90 text-white rounded-full flex items-center justify-center hover:scale-125 hover:-rotate-6 transition-all shadow-xl border border-white/10"
            >
              <FaGithub size={18} />
            </motion.a>
            <motion.a
              href={project.links.demo}
              target="_blank"
              animate={isHovered ? { scale: 1 } : { scale: 0 }}
              transition={{ delay: 0.18, type: "spring", stiffness: 200 }}
              className="w-10 h-10 bg-[#4779c4] text-white rounded-full flex items-center justify-center hover:scale-125 hover:rotate-6 transition-all shadow-xl"
            >
              <FaExternalLinkAlt size={16} />
            </motion.a>
          </div>
        </div>

        {/* Project Content */}
        <div 
          className="p-5 flex-grow flex flex-col"
          style={{ transform: isHovered ? "translateZ(30px)" : "none", transformStyle: "preserve-3d" }}
        >
          <h3 className="text-xl font-bold text-white mb-3 font-['Lora'] group-hover:text-[#4779c4] transition-colors">
            {project.title}
          </h3>
          <p className="text-sm text-slate-300 mb-4 flex-grow font-['Lora'] line-clamp-3">
            {project.description}
          </p>
          
          {/* Tech Stack Tags */}
          <div className="flex flex-wrap gap-2">
            {project.tags.map((tag, tagIndex) => (
              <motion.span
                key={tag}
                animate={isHovered ? { y: -4, boxShadow: "0 4px 12px rgba(71,121,196,0.2)" } : { y: 0, boxShadow: "none" }}
                transition={{ delay: tagIndex * 0.06, duration: 0.2 }}
                className="text-[10px] font-semibold px-3 py-1 rounded-full bg-slate-800/60 text-slate-300 border border-slate-700 transition-colors"
              >
                {tag}
              </motion.span>
            ))}
          </div>
        </div>
      </motion.div>

      <style>{`
        @keyframes bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-bob {
          animation: bob 2.5s ease-in-out infinite;
        }
        @keyframes shimmer {
          0% { transform: translateX(-150%); }
          100% { transform: translateX(150%); }
        }
        .shimmer-badge::after {
          content: '';
          position: absolute;
          top: 0; left: 0; width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
          animation: shimmer 3s infinite 2s;
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: inset 0 0 20px rgba(71,121,196,0.1); }
          50% { box-shadow: inset 0 0 35px rgba(71,121,196,0.25); }
        }
        .animate-pulse-glow {
          animation: pulse-glow 1.5s infinite ease-in-out;
        }
      `}</style>
    </motion.div>
  );
};

export default function Projects() {
  const [projectsList, setProjectsList] = useState(getProjects());
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const handleUpdate = () => {
      setProjectsList(getProjects());
    };
    window.addEventListener('projects_updated', handleUpdate);
    return () => window.removeEventListener('projects_updated', handleUpdate);
  }, []);

  const displayedProjects = showAll ? projectsList : projectsList.slice(0, 3);

  return (
    <section id="projects" className="relative py-20 px-4 sm:px-6 lg:px-8 bg-transparent overflow-hidden">
      {/* Background Decor */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#4779c4]/5 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-20 right-10 w-64 h-64 bg-[#4779c4]/5 rounded-full blur-[100px]"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#4779c4] text-sm font-bold tracking-[0.3em] uppercase mb-3">Portfolio</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 font-['Lora']">
            Featured Projects
          </h2>
          <div className="w-20 h-1 bg-[#4779c4] mx-auto rounded-full mb-8"></div>
          <p className="text-slate-400 text-sm sm:text-base max-w-2xl mx-auto font-['Lora'] italic">
            "Design is not just what it looks like and feels like. Design is how it works."
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedProjects.map((project, index) => (
            <ProjectCard key={project.id || project.title} project={project} index={index} />
          ))}
        </div>

        {/* Call to Action */}
        {projectsList.length > 3 && (
          <div className="text-center mt-20">
            <button
              onClick={() => setShowAll(!showAll)}
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2c456b]/30 text-white font-bold rounded-2xl hover:bg-[#4779c4] transition-all duration-300 border border-white/5 hover:border-[#4779c4] group"
            >
              <span>{showAll ? 'Show Less' : 'See More'}</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}